/**
 * Language list, storage and detection.
 *
 * Deliberately free of antd imports so the resolution logic can be exercised by
 * `scripts/check-languages.mjs` under plain node. The antd locale binding lives in
 * `src/theme/antd.js`.
 */

/**
 * The four locales the product ships, in menu order.
 *
 * `label` is the endonym (what a speaker of that language calls it) and `native` is its English
 * name, shown as a secondary hint. English needs no hint, hence null.
 */
export const LANGS = [
  { code: "en", label: "English", native: null },
  { code: "uk", label: "Українська", native: "Ukrainian" },
  { code: "hy", label: "Հայերեն", native: "Armenian" },
  { code: "ka", label: "ქართული", native: "Georgian" },
];

export const DEFAULT_LANG = "en";

export const isKnownLang = code => LANGS.some(l => l.code === code);

/** Presence of this key means the user picked a language by hand — see resolveLanguage(). */
export const LANG_STORAGE_KEY = "picsell.lang";

/**
 * IANA time zone → language, for the three countries that get their own locale.
 *
 * Each of these countries has a single zone, which is what makes the mapping viable. The legacy
 * aliases are not optional: plenty of systems still report `Europe/Kiev` rather than the current
 * `Europe/Kyiv` (the machine this was written on does).
 *
 * `Europe/Simferopol` is listed by IANA under Ukraine, so it maps to Ukrainian.
 */
const TZ_TO_LANG = {
  // Ukraine
  "Europe/Kyiv": "uk",
  "Europe/Kiev": "uk", // legacy alias, still widely reported
  "Europe/Uzhgorod": "uk", // legacy
  "Europe/Uzhhorod": "uk",
  "Europe/Zaporozhye": "uk", // legacy
  "Europe/Simferopol": "uk",
  // Armenia
  "Asia/Yerevan": "hy",
  // Georgia
  "Asia/Tbilisi": "ka",
};

/**
 * Approximate location → language, from the device time zone. Everything outside Ukraine,
 * Armenia and Georgia falls back to English.
 *
 * Time zone is a *proxy* for location, not a fix on it: it reflects how the device is
 * configured, so a traveller's laptop or a VPN-shifted clock will read as wherever the OS
 * thinks it is. That is the accepted trade — it costs nothing, needs no permission prompt, no
 * network call and no third-party service, and the user can always override it by hand.
 *
 * @param {string} [timeZone] Override, for tests.
 */
export function detectLanguage(timeZone) {
  try {
    const tz = timeZone || Intl.DateTimeFormat().resolvedOptions().timeZone;
    return TZ_TO_LANG[tz] || DEFAULT_LANG;
  } catch {
    /* Intl is missing or the zone is unresolvable — English is the documented fallback. */
    return DEFAULT_LANG;
  }
}

/** The stored language, or null when the user has never chosen one. */
export function readChosenLanguage(storage = safeStorage()) {
  try {
    const saved = storage?.getItem(LANG_STORAGE_KEY);
    return isKnownLang(saved) ? saved : null;
  } catch {
    return null;
  }
}

export function storeChosenLanguage(code, storage = safeStorage()) {
  try {
    storage?.setItem(LANG_STORAGE_KEY, code);
  } catch {
    /* Private mode or a full quota — the choice just won't survive a reload. */
  }
}

/**
 * The language to show before sign-in.
 *
 * An explicit choice always wins, and it is only ever written when the user picks from the menu
 * — so the mere presence of the key is what marks the choice as deliberate. Nothing detected is
 * ever persisted, which is what keeps "chosen" and "guessed" from blurring together.
 */
export function resolveLanguage(opts = {}) {
  return readChosenLanguage(opts.storage) || detectLanguage(opts.timeZone);
}

function safeStorage() {
  try {
    return typeof localStorage === "undefined" ? null : localStorage;
  } catch {
    /* Some privacy modes throw on mere access. */
    return null;
  }
}
